import { ArrowPathIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import ToggleButton from "@via/components/toggle-button";
import { ReportBrowser } from "@via/pages/projects/runs/reports/report-browser.tsx";
import { EMPTY_TABLE_MESSAGE, RunTabs } from "@via/utils/constants.tsx";
import {
  RunStatus,
  type RunReportResource,
  type GetRunDetailsResponseBody,
} from "common";
import { type FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRunReportList } from "@via/pages/projects/runs/runs.api.tsx";
import { type SelectOption } from "@via/utils/interfaces.tsx";
import { Tooltip } from "react-tooltip";
import UploadReportModal from "@via/pages/projects/runs/reports/upload-report-modal.tsx";
import { useUserConfigurations } from "@via/api/configuration.tsx";

interface RunReportsProps {
  runSettingsData: GetRunDetailsResponseBody;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  selectedRunAttempt?: SelectOption;
  runStatus?: RunStatus;
  activeRunTab?: RunTabs;
}

const RunReports: FC<RunReportsProps> = ({
  runSettingsData,
  isRunSettingsLoading,
  isRunSettingsError,
  selectedRunAttempt,
  runStatus,
  activeRunTab,
}) => {
  const { runId = "" } = useParams();

  const [reportTotal, setReportTotal] = useState(0);
  const [reportTree, setReportTree] = useState<RunReportResource[]>([]);
  const [reportTreeGroupByProcessName, setReportTreeGroupByProcessName] =
    useState<RunReportResource[]>([]);
  const [reportDisplayType, setReportDisplayType] = useState("reportTree");
  const [isUploadReportModalOpen, setIsUploadReportModalOpen] = useState(false);
  // Used to re-render treeStructure for show process name switches
  const [toggleCount, setToggleCount] = useState(0);
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  const {
    data: runReportsData,
    isSuccess: isRunReportsSuccess,
    isLoading: isRunReportsLoading,
    isError: isRunReportsError,
    refetch: refetchRunReports,
  } = useRunReportList({
    runId,
    attemptId: selectedRunAttempt?.value,
    enabled: activeRunTab === RunTabs.Reports,
    triggerSync: isManualRefresh,
  });
  const handleManualRefresh = () => {
    setIsManualRefresh(true);
    refetchRunReports().finally(() => {
      setIsManualRefresh(false);
    });
  };

  const { data: userConfigurations } = useUserConfigurations();

  useEffect(() => {
    if (selectedRunAttempt && activeRunTab === RunTabs.Reports) {
      refetchRunReports();
    }
  }, [selectedRunAttempt, refetchRunReports, activeRunTab]);

  useEffect(() => {
    if (
      activeRunTab === RunTabs.Reports &&
      (runStatus === RunStatus.NextError ||
        runStatus === RunStatus.Error ||
        runStatus === RunStatus.NextSuccess ||
        runStatus === RunStatus.Terminated)
    ) {
      refetchRunReports();
    }
  }, [runStatus, refetchRunReports, activeRunTab]);

  useEffect(() => {
    if (isRunReportsSuccess) {
      setReportTotal(runReportsData.total);
      setReportTree(runReportsData.data);

      const groupByProcessName = (data): RunReportResource[] => {
        const groupedData: Record<string, RunReportResource[]> = data.reduce(
          (acc, item) => {
            const key = item.processName
              .split("_")
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push({
              id: item.id,
              name: item.name,
              extension: "dir",
              appFileExtension: item.appFileExtension,
              processName: "",
              fileSize: 0,
              reportTypes: item.reportTypes,
              children: item.children,
            });
            return acc;
          },
          {}
        );

        return Object.entries(groupedData).map(([key, value]) => ({
          id: key,
          name: key,
          reportTypes: [],
          children: value,
          extension: "dir",
          appFileExtension: null,
          routePath: "",
          fileSize: 0,
          processName: key,
        }));
      };

      const groupedData = groupByProcessName(runReportsData.data);
      setReportTreeGroupByProcessName(groupedData);
    }
  }, [isRunReportsSuccess, runReportsData]);

  return (
    <main className="pt-4">
      {isRunReportsLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isRunReportsError ? (
        <div className="via-generic-error w-full justify-center">
          <GenericError message={EMPTY_TABLE_MESSAGE.REPORTS} />
        </div>
      ) : (
        <div>
          {runReportsData?.total > 0 && (
            <div className="mb-2 flex items-center">
              <ToggleButton
                isOn={reportDisplayType === "reportTreeGroupByProcessName"}
                onToggle={value => {
                  setReportDisplayType(
                    value ? "reportTreeGroupByProcessName" : "reportTree"
                  );
                  setToggleCount(1);
                }}
              />
              <span className="ml-2 text-sm font-medium leading-6 text-gray-700">
                Show Process Name
              </span>
            </div>
          )}
          <h2 className="via-h2 flex items-center">
            Total Reports: {reportTotal}
            <button type="button" onClick={handleManualRefresh}>
              <ArrowPathIcon
                id="refresh-environment"
                className="stroke-2.5 ml-1 h-5 w-5 stroke-current text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                aria-hidden="true"
              />
            </button>
            {userConfigurations?.UPLOAD_REPORT && (
              <button
                type="button"
                onClick={() => setIsUploadReportModalOpen(true)}>
                <ArrowUpTrayIcon
                  id="upload-report"
                  className="stroke-2.5 ml-2 h-5 w-5 stroke-current text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                  aria-hidden="true"
                />
              </button>
            )}
          </h2>
          <Tooltip
            anchorSelect="#refresh-environment"
            render={() => <>Refresh Reports</>}
          />
          <Tooltip
            anchorSelect="#upload-report"
            render={() => <>Upload a Report</>}
          />
          <ReportBrowser
            reportTree={
              reportDisplayType === "reportTree"
                ? reportTree
                : reportTreeGroupByProcessName
            }
            runSettingsData={runSettingsData}
            isRunSettingsLoading={isRunSettingsLoading}
            isRunSettingsError={isRunSettingsError}
            openByDefault={toggleCount ? true : false}
            activeRunTab={activeRunTab}
          />
          {isUploadReportModalOpen && (
            <UploadReportModal
              open={isUploadReportModalOpen}
              setOpen={setIsUploadReportModalOpen}
              runUUID={runSettingsData.runUUID as string}
            />
          )}
        </div>
      )}
    </main>
  );
};

export default RunReports;
